import {
  FilterValidationErrorEnum,
  ListFilterOperatorEnum,
  NumberFilterOperatorEnum,
  StringFilterOperatorEnum,
} from "./enums";
import {
  ISmartTableFilterLabels,
  TConditionOperator,
  TFilter,
  TFilterCondition,
  TFilterGroup,
  TNewFilterCondition,
  ValidationResult,
} from "./types";

import { DEFAULT_LABELS } from "./constants";
import { ITableField } from "../../types/IFields";
import { generateRandomKey } from "@tedivo/tedivo-pure-helpers";

const TypedFilterTreeEventTarget = EventTarget as {
  new (): IFilterTreeEventTarget;
  prototype: IFilterTreeEventTarget;
};

/** Class for Data Filters */
export class FilterTree<TData> extends TypedFilterTreeEventTarget {
  public root: TFilterGroup<TData>;

  private idSet: Set<string>;
  private labels: ISmartTableFilterLabels;
  private intDel = 0;
  private fieldsByKey: Map<keyof TData, ITableField<TData>> | undefined;

  constructor({
    filterTree,
    labels,
    fieldsByKey,
  }: {
    filterTree?: TFilterGroup<TData>;
    labels?: ISmartTableFilterLabels;
    fieldsByKey?: Map<keyof TData, ITableField<TData>>;
  }) {
    super();

    this.root = filterTree
      ? this.deepClone(filterTree)
      : { id: generateRandomKey(), type: "AND", children: [] };

    this.idSet = new Set(this.collectIds(this.root));

    this.labels = labels ?? DEFAULT_LABELS;

    this.fieldsByKey = fieldsByKey;

    if (filterTree) {
      this.dispatchCustomEvent("updated", { action: "initialized" });
    }
  }

  // Deep clone method to ensure a clean new filter tree
  private deepClone(filter: TFilterGroup<TData>): TFilterGroup<TData> {
    if ("structuredClone" in globalThis) {
      return globalThis.structuredClone(filter);
    } else {
      return {
        id: filter.id,
        type: filter.type,
        children: filter.children.map((child) =>
          "children" in child ? this.deepClone(child) : { ...child },
        ),
      };
    }
  }

  // 📌 Collect all IDs in a set (for fast lookup)
  private collectIds(filter: TFilterGroup<TData>): string[] {
    return [
      filter.id,
      ...filter.children.flatMap((child) =>
        "children" in child ? this.collectIds(child) : [child.id],
      ),
    ];
  }

  getFilters(): TFilter<TData> {
    return this.deepClone(this.root); // Return a safe deep copy
  }

  // 🛠 Function to replace all filters at once
  setFilters(newFilter: TFilterGroup<TData>) {
    this.root = this.deepClone(newFilter); // 🔄 Replace with a clean deep clone
    this.idSet = new Set(this.collectIds(this.root)); // ✅ Update ID set

    this.dispatchCustomEvent("updated", { action: "replace" });
  }

  passesFilter(item: TData): boolean {
    const evaluate = (
      node: TFilterGroup<TData> | TFilterCondition<TData>,
    ): boolean => {
      if ("children" in node) {
        const results = node.children.map(evaluate);
        return node.type === "AND"
          ? results.every(Boolean)
          : results.some(Boolean);
      }

      if (!node.field || !node.operator) return false;

      const operator = node.operator;

      const fieldValue = getFieldValueFromItem(item, node.field);

      if (fieldValue == null) return false;

      if (
        operator in StringFilterOperatorEnum ||
        operator in NumberFilterOperatorEnum
      ) {
        const valueStr =
          typeof fieldValue === "string"
            ? fieldValue.toLowerCase()
            : String(fieldValue).toLowerCase();
        const compareTo = String(node.value ?? "").toLowerCase();

        switch (operator) {
          case StringFilterOperatorEnum.CONTAINS:
          case NumberFilterOperatorEnum.CONTAINS:
            return valueStr.includes(compareTo);

          case StringFilterOperatorEnum.EQUALS:
          case NumberFilterOperatorEnum.EQUALS:
            return valueStr === compareTo;

          case StringFilterOperatorEnum.STARTS_WITH:
            return valueStr.startsWith(compareTo);

          case StringFilterOperatorEnum.ENDS_WITH:
            return valueStr.endsWith(compareTo);

          case NumberFilterOperatorEnum.GREATER_THAN:
            return Number(fieldValue) > Number(node.value);

          case NumberFilterOperatorEnum.LESS_THAN:
            return Number(fieldValue) < Number(node.value);

          case NumberFilterOperatorEnum.GREATER_THAN_OR_EQUAL:
            return Number(fieldValue) >= Number(node.value);

          case NumberFilterOperatorEnum.LESS_THAN_OR_EQUAL:
            return Number(fieldValue) <= Number(node.value);

          default:
            return false;
        }
      }

      if (
        (operator === ListFilterOperatorEnum.CONTAINS ||
          operator === ListFilterOperatorEnum.NOT_CONTAINS) &&
        Array.isArray(node.value)
      ) {
        const fieldStr = String(fieldValue);
        const values = node.value.map(String);

        return operator === ListFilterOperatorEnum.CONTAINS
          ? values.includes(fieldStr)
          : !values.includes(fieldStr);
      }

      return false;
    };

    return evaluate(this.root);
  }

  // Add a condition of any type
  addCondition(
    condition: TNewFilterCondition<TData>,
    parentId: string | null = null,
  ) {
    const newCondition: TFilterCondition<TData> = {
      id: generateRandomKey(),
      ...condition,
    };

    this.idSet.add(newCondition.id); // ✅ Update ID set

    if (!parentId) {
      this.root.children.push(newCondition);
    } else if (this.idSet.has(parentId)) {
      const parent = this.findNodeById(parentId);
      if (parent && "children" in parent) {
        parent.children.push(newCondition);
      }
    }

    this.dispatchCustomEvent("updated", { action: "add" });
  }

  // Add a logical AND/OR group
  addGroup(type: "AND" | "OR", parentId: string | null = null): string {
    const newGroup: TFilterGroup<TData> = {
      id: generateRandomKey(),
      type,
      children: [],
    };

    this.idSet.add(newGroup.id); // ✅ Update ID set

    if (!parentId) {
      this.root.children.push(newGroup);
    } else if (this.idSet.has(parentId)) {
      const parent = this.findNodeById(parentId);
      if (parent && "children" in parent) {
        parent.children.push(newGroup);
      }
    }

    this.dispatchCustomEvent("updated", { action: "addGroup" });

    return newGroup.id;
  }

  // Insert a node in the middle of an existing group
  insertNodeInMiddle(targetId: string, newType: "AND" | "OR"): string {
    if (!this.idSet.has(targetId)) return ""; // 🚀 Skip if ID doesn’t exist

    const targetNode = this.findNodeById(targetId);
    if (!targetNode || !("children" in targetNode)) return "";

    const newGroup: TFilterGroup<TData> = {
      id: generateRandomKey(),
      type: newType,
      children: [targetNode], // Wrap the target node inside the new group
    };

    this.idSet.add(newGroup.id); // ✅ Update ID set

    // Find the parent group and replace the target node with the new group
    const parentNode = this.findParentNode(targetId);
    if (parentNode && "children" in parentNode) {
      const index = parentNode.children.findIndex(
        (child) => child.id === targetId,
      );
      if (index !== -1) {
        parentNode.children[index] = newGroup;
      }
    }

    this.dispatchCustomEvent("updated", { action: "insertNodeInMiddle" });

    return newGroup.id;
  }

  // 🛠 Edit an existing node
  updateNode(
    nodeId: string,
    newCondition: Partial<TNewFilterCondition<TData>>,
  ): boolean {
    if (!this._checkIfIdExists(nodeId)) return false;

    const node = this.findNodeById(nodeId);
    if (node && !("children" in node)) {
      Object.assign(node, newCondition); // Update node fields
      this.dispatchCustomEvent("updated", { action: "updateNode" });

      return true;
    }

    return false;
  }

  moveNode(nodeId: string, newParentId: string): boolean {
    if (!this._checkIfIdExists(nodeId) || !this.idSet.has(newParentId))
      return false;

    const node = this.findNodeById(nodeId);
    const newParent = this.findNodeById(newParentId);

    if (!node || !newParent || !("children" in newParent)) return false;

    const oldParent = this.findParentNode(nodeId);
    if (!oldParent) return false;

    // Remove from old parent
    const oldIndex = oldParent.children.findIndex((c) => c.id === nodeId);
    if (oldIndex === -1) return false;

    const [removedNode] = oldParent.children.splice(oldIndex, 1);
    newParent.children.push(removedNode);

    // Clean up structure in case oldParent is now redundant
    if (oldParent.children.length === 1) {
      const onlyChild = oldParent.children[0];
      const grandParent = this.findParentNode(oldParent.id);
      if (grandParent && "children" in grandParent) {
        const idx = grandParent.children.findIndex(
          (c) => c.id === oldParent.id,
        );
        if (idx !== -1) {
          grandParent.children[idx] = onlyChild;
          this.idSet.delete(oldParent.id);
        }
      }
    }

    this.normalizeTree();

    this.dispatchCustomEvent("updated", { action: "moveNode" });

    return true;
  }

  insertEmptyCondition(parentGroupId: string, index?: number): string {
    const parent = this.findNodeById(parentGroupId);

    if (!parent || !("children" in parent)) {
      console.warn(
        `Cannot insert condition — parent group ${parentGroupId} not found`,
      );
      return "";
    }

    const newCondition: TFilterCondition<TData> = {
      id: generateRandomKey(),
      field: undefined,
      operator: undefined,
      value: undefined,
    };

    this.idSet.add(newCondition.id);

    if (index != null && index >= 0 && index <= parent.children.length) {
      parent.children.splice(index, 0, newCondition);
    } else {
      parent.children.push(newCondition);
    }

    return newCondition.id;
  }

  wrapWithGroupAndAddEmptyCondition(
    conditionId: string,
    groupType: "AND" | "OR",
  ): string | null {
    if (!this._checkIfIdExists(conditionId)) return null;

    const conditionNode = this.findNodeById(conditionId);
    const parentNode = this.findParentNode(conditionId);

    if (!conditionNode || !parentNode || !("children" in parentNode))
      return null;

    const conditionIndex = parentNode.children.findIndex(
      (c) => c.id === conditionId,
    );
    if (conditionIndex === -1) return null;

    // Create new empty condition
    const emptyCondition: TFilterCondition<TData> = {
      id: generateRandomKey(),
      field: "" as keyof TData & string,
      operator: StringFilterOperatorEnum.EQUALS,
      value: "",
    };
    this.idSet.add(emptyCondition.id);

    // Create new group with both conditions
    const newGroup: TFilterGroup<TData> = {
      id: generateRandomKey(),
      type: groupType,
      children: [conditionNode, emptyCondition],
    };
    this.idSet.add(newGroup.id);

    // Replace the original condition with the new group in the parent
    parentNode.children.splice(conditionIndex, 1, newGroup);

    return emptyCondition.id;
  }

  removeNode(nodeId: string): boolean {
    if (!this._checkIfIdExists(nodeId)) return false;

    const parentNode = this.findParentNode(nodeId);
    const success = this.removeNodeRecursively(this.root, nodeId);
    if (!success) return false;

    this.idSet.delete(nodeId);
    this.dispatchCustomEvent("updated", { action: "remove" });

    if (!parentNode || parentNode.children.length !== 1) return true;

    const onlyChild = parentNode.children[0];
    const grandParent = this.findParentNode(parentNode.id);

    // Case 1: Flatten AND inside OR if only child is OR
    if (
      parentNode.type === "AND" &&
      grandParent?.type === "OR" &&
      "type" in onlyChild &&
      onlyChild.type === "OR"
    ) {
      const index = grandParent.children.findIndex(
        (c) => c.id === parentNode.id,
      );
      if (index !== -1) {
        grandParent.children.splice(index, 1, ...onlyChild.children);
        this.idSet.delete(parentNode.id);
        this.idSet.delete(onlyChild.id);
      }

      this.flattenRedundantGroups(grandParent);
    }

    // Case 2: Flatten OR if it's left with one group-child
    else if (
      grandParent &&
      parentNode.type === "OR" &&
      "type" in onlyChild // Ensure it's a group
    ) {
      const index = grandParent?.children.findIndex(
        (c) => c.id === parentNode.id,
      );
      if (index && index !== -1 && grandParent) {
        grandParent.children.splice(index, 1, onlyChild);
        this.idSet.delete(parentNode.id);
      }

      this.flattenRedundantGroups(grandParent);
    }

    // 🔁 CASE 3: Flatten AND inside AND (merge inner AND's children into outer AND)
    if (
      grandParent &&
      parentNode.type === "AND" &&
      "type" in onlyChild &&
      onlyChild.type === "AND"
    ) {
      // Replace the nested AND with its children
      parentNode.children = onlyChild.children;
      this.idSet.delete(onlyChild.id);

      this.flattenRedundantGroups(grandParent);
    }

    // 🔁 DEFAULT: Replace parent with its only child
    if (grandParent && "children" in grandParent) {
      const index = grandParent.children.findIndex(
        (c) => c.id === parentNode.id,
      );
      if (index !== -1) {
        grandParent.children[index] = onlyChild;
        this.idSet.delete(parentNode.id);
        this.flattenRedundantGroups(grandParent);
      }
    }

    this.normalizeTree();
    return true;
  }

  getValidationResults(): ValidationResult {
    const issues: { id: string; errors: FilterValidationErrorEnum[] }[] = [];

    const validateCondition = (cond: TFilterCondition<TData>) => {
      const errors: FilterValidationErrorEnum[] = [];

      if (!cond.field) {
        errors.push(FilterValidationErrorEnum.MISSING_FIELD);
      }

      if (!cond.operator) {
        errors.push(FilterValidationErrorEnum.MISSING_OPERATOR);
      }

      if (
        (cond.value === undefined &&
          cond.operator !== StringFilterOperatorEnum.EQUALS &&
          cond.operator !== NumberFilterOperatorEnum.EQUALS) ||
        (Array.isArray(cond.value) && cond.value.length === 0) ||
        cond.value === ""
      ) {
        errors.push(FilterValidationErrorEnum.MISSING_VALUE);
      }

      if (errors.length > 0) {
        issues.push({ id: cond.id, errors });
      }
    };

    const walk = (node: TFilterGroup<TData> | TFilterCondition<TData>) => {
      if ("children" in node) {
        node.children.forEach(walk);
      } else {
        validateCondition(node);
      }
    };

    walk(this.root);
    return issues;
  }

  findParentNode(targetId: string): TFilterGroup<TData> | null {
    return this.findParentNodeRecursively(this.root, targetId);
  }

  private flattenRedundantGroups(group: TFilterGroup<TData>) {
    group.children = group.children.flatMap((child) => {
      if ("type" in child && child.type === group.type) {
        this.idSet.delete(child.id);
        return child.children;
      }
      return [child];
    });
  }

  private removeNodeRecursively(
    parent: TFilterGroup<TData>,
    nodeId: string,
  ): boolean {
    parent.children = parent.children.filter((child) => {
      if (child.id === nodeId) return false;
      if ("children" in child) {
        return this.removeNodeRecursively(child, nodeId);
      }
      return true;
    });
    return true;
  }

  private _checkIfIdExists(nodeId: string): boolean {
    if (!this.idSet.has(nodeId)) {
      console.error(
        `Something went wrong. Condition with id ${nodeId} not found in the current filter.`,
      );
      return false;
    }
    return true;
  }

  // 🔍 Find a node by ID
  private findNodeById(
    id: string,
  ): TFilterGroup<TData> | TFilterCondition<TData> | null {
    return this.findNodeByIdRecursively(this.root, id);
  }

  private findNodeByIdRecursively(
    node: TFilterGroup<TData> | TFilterCondition<TData>,
    id: string,
  ): TFilterGroup<TData> | TFilterCondition<TData> | null {
    if (node.id === id) return node;
    if ("children" in node) {
      for (const child of node.children) {
        const found = this.findNodeByIdRecursively(child, id);
        if (found) return found;
      }
    }
    return null;
  }

  private findParentNodeRecursively(
    parent: TFilterGroup<TData>,
    targetId: string,
  ): TFilterGroup<TData> | null {
    for (const child of parent.children) {
      if (child.id === targetId) return parent;
      if ("children" in child) {
        const found = this.findParentNodeRecursively(child, targetId);
        if (found) return found;
      }
    }
    return null;
  }

  private normalizeGroup(group: TFilterGroup<TData>): TFilterGroup<TData> {
    const normalizedChildren: (
      | TFilterCondition<TData>
      | TFilterGroup<TData>
    )[] = [];

    for (const child of group.children) {
      if ("children" in child) {
        const normalized = this.normalizeGroup(child);

        // Flatten same-type groups (AND inside AND, OR inside OR)
        if (normalized.type === group.type) {
          this.idSet.delete(normalized.id);
          normalizedChildren.push(...normalized.children);
        }
        // OR group with a single child that is a group (e.g., OR > AND)
        else if (
          group.type === "OR" &&
          normalized.children.length === 1 &&
          "children" in normalized.children[0]
        ) {
          const grandChild = normalized.children[0] as TFilterGroup<TData>;
          this.idSet.delete(normalized.id);
          normalizedChildren.push(grandChild);
        } else {
          normalizedChildren.push(normalized);
        }
      } else {
        normalizedChildren.push(child);
      }
    }

    return {
      ...group,
      children: normalizedChildren,
    };
  }

  private normalizeTree() {
    this.root = this.normalizeGroup(this.root);
    this.idSet = new Set(this.collectIds(this.root));
  }

  // Tells if the filter is empty with no failing rules
  isEmptyFilter(): boolean {
    const walk = (
      node: TFilterGroup<TData> | TFilterCondition<TData>,
    ): boolean => {
      if ("children" in node) {
        return node.children.every(walk);
      }

      return (
        !node.field &&
        !node.operator &&
        (node.value === undefined || node.value === "")
      );
    };

    return walk(this.root);
  }

  toPrettyHtmlElement(
    renderField?: (f: keyof TData) => string,
    renderValue?: (v: unknown, f: keyof TData) => string,
    opts: { inline?: boolean } = {},
  ): HTMLElement {
    const isInline = opts.inline ?? true;
    const operatorColor = "var(--filter-color-operator)";
    let parenLevel = 0;

    const getParenColor = (level: number) =>
      `var(--filter-color-paren-${(level % 15) + 1})`;

    const formatCondition = (
      cond: TFilterCondition<TData>,
      depth: number,
    ): HTMLElement => {
      const field =
        cond.field && renderField
          ? renderField(cond.field)
          : String(cond.field ?? " -?- ");

      const op = cond.operator && this.operatorToText(cond.operator ?? "-?-");

      const value = Array.isArray(cond.value)
        ? `[${cond.value
            .map(
              (v) =>
                `"${
                  renderValue ? renderValue(v, cond.field as keyof TData) : v
                }"`,
            )
            .join(", ")}]`
        : `"${
            renderValue
              ? renderValue(cond.value, cond.field as keyof TData)
              : cond.value ?? "-?-"
          }"`;

      const line = document.createElement(isInline ? "span" : "div");
      line.className = isInline ? "inline-condition-line" : "condition-line";
      if (!isInline) line.style.setProperty("--depth", `${depth}`);

      const f = document.createElement("span");
      f.className = "condition-field";
      f.textContent = field;

      const o = document.createElement("span");
      o.className = "condition-operator";
      o.textContent = op ?? "?";

      const v = document.createElement("span");
      v.className = "condition-value";
      v.textContent = value ?? "?";

      line.appendChild(f);
      line.appendChild(o);
      line.appendChild(v);

      return line;
    };

    const wrapParens = (
      children: HTMLElement[],
      parenDepth: number,
      type: "AND" | "OR",
      depth: number,
    ): HTMLElement => {
      const group = document.createElement(isInline ? "span" : "div");
      group.className = isInline
        ? "inline-filter-group"
        : "filter-group-container";
      const color = getParenColor(parenDepth);

      const open = document.createElement(isInline ? "span" : "div");
      open.className = "parenthesis";
      open.textContent = "(";
      open.style.color = color;
      if (!isInline) open.style.setProperty("--parent-indent", `${depth}`);

      const close = document.createElement(isInline ? "span" : "div");
      close.className = "parenthesis";
      close.textContent = ")";
      close.style.color = color;
      if (!isInline) close.style.setProperty("--parent-indent", `${depth}`);

      group.appendChild(open);

      children.forEach((el, i) => {
        group.appendChild(el);

        if (i < children.length - 1) {
          const joiner = document.createElement(isInline ? "span" : "div");
          joiner.className = "joiner";
          joiner.textContent = this.labels[type === "AND" ? "and" : "or"];
          joiner.style.color = operatorColor;
          if (!isInline) joiner.style.setProperty("--depth", `${depth + 1}`);
          group.appendChild(joiner);
        }
      });

      group.appendChild(close);
      return group;
    };

    const walk = (
      node: TFilterGroup<TData> | TFilterCondition<TData>,
      depth = 0,
    ): HTMLElement => {
      if ("children" in node) {
        parenLevel++;
        const children = node.children.map((child) => walk(child, depth + 1));
        const wrapped = wrapParens(children, parenLevel, node.type, depth);
        parenLevel--;
        return wrapped;
      } else {
        return formatCondition(node, depth);
      }
    };

    const outer = document.createElement(isInline ? "span" : "div");
    outer.className = isInline
      ? "inline-filter-summary"
      : "filter-text-wrapper";

    if (isInline) {
      outer.style.whiteSpace = "nowrap";
      outer.style.overflow = "hidden";
      outer.style.textOverflow = "ellipsis";
      outer.style.display = "inline-block";
      outer.style.maxWidth = "100%";
    }

    const wrapper = document.createElement(isInline ? "span" : "div");
    wrapper.className = isInline ? "inline-filter-wrapper" : "filter-text-area";

    wrapper.appendChild(walk(this.root));
    outer.appendChild(wrapper);
    return outer;
  }

  private operatorToText(op: TConditionOperator): string {
    const l = this.labels?.operators;

    switch (op) {
      case StringFilterOperatorEnum.CONTAINS:
        return l?.StringFilterOperatorEnum.CONTAINS ?? "contains";
      case StringFilterOperatorEnum.EQUALS:
        return "=";
      case StringFilterOperatorEnum.STARTS_WITH:
        return l?.StringFilterOperatorEnum.STARTS_WITH ?? "starts with";
      case StringFilterOperatorEnum.ENDS_WITH:
        return l?.StringFilterOperatorEnum.ENDS_WITH ?? "ends with";

      case NumberFilterOperatorEnum.CONTAINS:
        return l?.NumberFilterOperatorEnum.CONTAINS ?? "contains";
      case NumberFilterOperatorEnum.EQUALS:
        return "=";
      case NumberFilterOperatorEnum.GREATER_THAN:
        return ">";
      case NumberFilterOperatorEnum.LESS_THAN:
        return "<";
      case NumberFilterOperatorEnum.GREATER_THAN_OR_EQUAL:
        return ">=";
      case NumberFilterOperatorEnum.LESS_THAN_OR_EQUAL:
        return "<=";

      case ListFilterOperatorEnum.CONTAINS:
        return l?.ListFilterOperatorEnum.IN ?? "is in";
      case ListFilterOperatorEnum.NOT_CONTAINS:
        return l?.ListFilterOperatorEnum.NOT_IN ?? "is not in";

      default:
        return String(op);
    }
  }

  private dispatchCustomEvent(
    type: string,
    detail: unknown,
    options?: CustomEventInit,
  ): void {
    if (this.intDel > 0) {
      clearTimeout(this.intDel);
    }

    this.intDel = window.setTimeout(() => {
      const event = new CustomEvent(type, {
        detail,
        bubbles: true,
        cancelable: true,
        composed: true,
        ...options,
      });

      this.dispatchEvent(event);
    }, 25);
  }

  // Get tree as JSON
  toJSON() {
    return this.root;
  }
}

interface IFilterTreeEventMapEventMap {
  updated: CustomEvent<{
    action:
      | "initialized"
      | "replace"
      | "add"
      | "addGroup"
      | "insertNodeInMiddle"
      | "updateNode"
      | "moveNode"
      | "remove";
  }>;
}

interface IFilterTreeEventTarget extends EventTarget {
  addEventListener<K extends keyof IFilterTreeEventMapEventMap>(
    type: K,
    listener: (ev: IFilterTreeEventMapEventMap[K]) => void,
    options?: boolean | AddEventListenerOptions,
  ): void;

  addEventListener(
    type: string,
    callback: EventListenerOrEventListenerObject | null,
    options?: EventListenerOptions | boolean,
  ): void;
}

export function getFieldValueFromItem<TData>(
  item: TData,
  fieldName: keyof TData,
  fieldsByKey?: Map<keyof TData, ITableField<TData>>,
): unknown | undefined {
  const field = fieldsByKey?.get(fieldName);

  if (!field) {
    return item[fieldName];
  } else if (field.type === "boolean") {
    return !!item[field.name];
  } else if (field.type === "number" || field.type === "inlineEditWithUnits") {
    return Number(item[field.name]);
  } else if (field.type === "date") {
    return item[field.name];
  } else {
    return String(
      field.sortValueFunction
        ? field.sortValueFunction(item)
        : field.valueFunction
        ? field.valueFunction(item)
        : item[field.name],
    ).toUpperCase();
  }
}
